// src/utils/checkoutHelpers.js
//
// Pure logic and constants for the Student Checkout system.
// No React, no Firebase imports — safe to import anywhere.
//
// roles.js re-exports the constants from this file so the rest of the app
// can import from a consistent location. When adding new sections or form
// types, edit this file.

// =============================================================================
// SECTION CATALOG — the universe of all possible signoff categories.
// Order here is the canonical display order on dashboards and PDFs.
//
// MS support: library, athletics, and finance gain 'ms' in their appliesTo
// arrays (same humans sign off for both US and MS). ms_final is the MS
// parallel to OSL — its own final-clearance section, with its own signers.
// =============================================================================

export const CHECKOUT_SECTIONS = [
  {
    id: 'scheduling',
    label: 'Scheduling',
    description: 'Course scheduling for next year',
    appliesTo: ['9-11', 'international'],
  },
  {
    id: 'science_books',
    label: 'Science — Returned Books',
    description: 'Returned science textbooks',
    appliesTo: ['9-11', 'senior', 'international'],
    group: 'Course Teacher',
  },
  {
    id: 'math_books',
    label: 'Mathematics — Returned Books',
    description: 'Returned math textbooks',
    appliesTo: ['9-11', 'senior', 'international'],
    group: 'Course Teacher',
  },
  {
    id: 'history_books',
    label: 'History — Returned Books',
    description: 'Returned history textbooks',
    appliesTo: ['9-11', 'senior', 'international'],
    group: 'Course Teacher',
  },
  {
    id: 'world_lang_books',
    label: 'World Languages — Returned Books',
    description: 'Returned world language textbooks',
    appliesTo: ['9-11', 'senior', 'international'],
    group: 'Course Teacher',
  },
  {
    id: 'art_books',
    label: 'Art Department — Returned Books',
    description: 'Returned art textbooks/materials',
    appliesTo: ['9-11', 'senior', 'international'],
    group: 'Course Teacher',
  },
  {
    id: 'athletics',
    label: 'Athletics',
    description: 'Returned uniforms and equipment',
    appliesTo: ['9-11', 'senior', 'international', 'ms'],
  },
  {
    id: 'library',
    label: 'Library',
    description: 'Returned checked-out books and movies',
    appliesTo: ['9-11', 'senior', 'international', 'ms'],
  },
  {
    id: 'uco',
    label: 'UCO',
    description:
      'Address to send transcripts to, ML is fully updated with all decisions, programs, and financial aid received.',
    appliesTo: ['senior'],
  },
  {
    id: 'advancement',
    label: 'Alumni Relations / Advancement',
    description: 'Post-graduate contact information',
    appliesTo: ['senior'],
  },
  {
    id: 'hr_residency',
    label: 'HR & General Admin',
    description: 'Return Residency Permit',
    appliesTo: ['international'],
  },
  {
    id: 'finance',
    label: 'Finance',
    description: 'Outstanding charges or fees with the Finance Office',
    appliesTo: ['9-11', 'senior', 'international', 'ms'],
  },
  {
    id: 'it',
    label: 'IT Help Desk',
    description: 'Return all loaner devices: computer, case, power supply, extra battery',
    appliesTo: ['9-11', 'senior', 'international'],
  },
  {
    id: 'dorm',
    label: 'Dorm Faculty',
    description:
      'Room inventory and condition (boarders only). Damage will incur a flat JD50 charge.',
    appliesTo: ['9-11', 'senior', 'international'],
    boardersOnly: true,
    hasInventoryChecklist: true,
  },
  {
    id: 'osl',
    label: 'OSL — Final Clearance',
    description:
      'This student has fulfilled all of his/her obligations and is free to leave.',
    appliesTo: ['9-11', 'senior', 'international'],
    isFinalSignoff: true, // triggers the override-user check when other sections are pending
  },
  {
    id: 'ms_final',
    label: 'Middle School — Final Clearance',
    description:
      'This student has fulfilled all middle school obligations and is free to leave.',
    appliesTo: ['ms'],
    isFinalSignoff: true, // same gating as OSL — separate signers
  },
];

// =============================================================================
// FORM TEMPLATES — the four variants of the checkout form.
//
// 'ms' is the Middle School variant. Used only as PDF/labeling metadata —
// section relevance is driven by the per-record sections map, not form type.
// =============================================================================

export const CHECKOUT_FORMS = [
  {
    id: '9-11',
    label: 'Grades 9–11 Exit Checklist',
    shortLabel: '9–11',
    description: 'For domestic students in grades 9, 10, and 11.',
  },
  {
    id: 'senior',
    label: 'Senior Exit Checklist',
    shortLabel: 'Senior',
    description: 'For graduating Grade 12 students.',
  },
  {
    id: 'international',
    label: 'International Students Exit Checklist',
    shortLabel: 'International',
    description: 'For international students (any grade).',
  },
  {
    id: 'ms',
    label: 'Middle School Exit Checklist',
    shortLabel: 'MS',
    description: 'For students in grades 7 and 8.',
  },
];

// =============================================================================
// DORM INVENTORY — checklist items for the dorm signoff modal.
// Mirrors the paper form's room inspection list.
// =============================================================================

export const DORM_INVENTORY_ITEMS = [
  { id: 'swept', label: 'Swept and free of trash' },
  { id: 'waste_basket', label: 'Waste basket' },
  { id: 'side_lamp', label: 'Side lamp' },
  { id: 'desk_lamp', label: 'Desk lamp' },
  { id: 'cupboard', label: 'Cupboard' },
  { id: 'safe', label: 'Safe' },
  { id: 'chest_of_drawers', label: 'Chest of drawers' },
  { id: 'bedside_drawers', label: 'Bedside drawers' },
  { id: 'table', label: 'Table' },
  { id: 'chair', label: 'Chair' },
  { id: 'shelf', label: 'Shelf' },
  { id: 'bed', label: 'Bed' },
  { id: 'window_screen', label: 'Window and screen' },
  { id: 'lock_door_handle', label: 'Lock and door handle' },
  { id: 'key_handed_in', label: 'Key handed in' },
];

// =============================================================================
// STATUS CONSTANTS
// =============================================================================

export const SECTION_STATUS = {
  PENDING: 'pending',
  SIGNED: 'signed',
  NA: 'na',
};

export const RECORD_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  ARCHIVED: 'archived',
};

// =============================================================================
// GRADE GROUP HELPERS
//
// MS = Middle School (grades 7-8). US = Upper School (grades 9-12).
// Used by the dashboard filter and the MS-scoped Checkout wrapper.
// =============================================================================

export const MS_GRADES = ['Grade 7', 'Grade 8'];
export const US_GRADES = ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];

export const isMsGrade = (grade) => MS_GRADES.includes(grade);
export const isUsGrade = (grade) => US_GRADES.includes(grade);

// =============================================================================
// LOOKUPS / DERIVED HELPERS
// =============================================================================

export const getSectionConfig = (sectionId) =>
  CHECKOUT_SECTIONS.find((s) => s.id === sectionId) || null;

export const getFormConfig = (formId) =>
  CHECKOUT_FORMS.find((f) => f.id === formId) || null;

/**
 * Returns the list of section IDs that are RELEVANT to a given checkout
 * record (i.e. not marked N/A). The record's `sections` map is the source
 * of truth. Driven by rosters at record-creation time + auto-rules for
 * universal/derived sections.
 *
 * This replaces the older form-type-driven version. Form type is now just
 * metadata (PDF labeling).
 */
export const getRelevantSectionIds = (record) => {
  if (!record?.sections) return [];
  return CHECKOUT_SECTIONS.filter((section) => {
    const status = record.sections[section.id]?.status;
    return status && status !== SECTION_STATUS.NA;
  }).map((s) => s.id);
};

/**
 * Older callers passed (formId, cohort) instead of a record object. For
 * backward compatibility — though there are no remaining callers — this
 * variant is kept under a different name.
 */
export const getDefaultSectionIdsForForm = (formId, cohort) => {
  const isBoarder = cohort === 'Boarding';
  return CHECKOUT_SECTIONS.filter((section) => {
    if (!section.appliesTo.includes(formId)) return false;
    if (section.boardersOnly && !isBoarder) return false;
    return true;
  }).map((s) => s.id);
};

/**
 * Suggest a default form variant for a given student.
 * Used by the FormAssignModal to pre-select an option.
 *
 * Heuristic:
 *   - Grade 7 or 8 → 'ms'  (MS takes precedence, regardless of any other flag)
 *   - Grade 12     → 'senior'
 *   - Grades 9-11  → '9-11'
 *   - else         → null
 *
 * The students collection currently has no explicit "international" flag,
 * so we default by grade and let the teacher override.
 */
export const suggestFormForStudent = (student) => {
  if (!student?.grade) return null;
  if (isMsGrade(student.grade)) return 'ms';
  if (student.grade === 'Grade 12') return 'senior';
  if (['Grade 9', 'Grade 10', 'Grade 11'].includes(student.grade)) return '9-11';
  return null;
};

// =============================================================================
// PERMISSION HELPERS
// =============================================================================

/**
 * Whether a given user can sign off a given section.
 *
 * @param {object} user      - { uid, isSuperAdmin, checkoutSections }
 * @param {string} sectionId
 */
export const canSignOffSection = (user, sectionId) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.checkoutSections ?? []).includes(sectionId);
};

/**
 * Whether the user has any signoff permissions at all.
 * Used to gate access to the StudentCheckout tool itself.
 */
export const hasAnyCheckoutPermission = (user) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.checkoutSections ?? []).length > 0;
};

/**
 * Whether the user can authorize an early final signoff (OSL or ms_final)
 * — i.e. sign the section before all other sections are complete.
 *
 * The list of users with this privilege is stored per-final-section in
 * /config/checkoutPointPeople.finalSignoffOverrides.{sectionId} and synced
 * to the user's user-doc as `checkoutOverrides`. Pass the specific
 * sectionId being signed (e.g. 'osl' or 'ms_final').
 */
export const canOverrideFinalSignoff = (user, sectionId) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.checkoutOverrides ?? []).includes(sectionId);
};

/**
 * Whether the user can edit the roster for a given section.
 * Roster admin permissions are managed separately from signer permissions —
 * see /config/checkoutPointPeople.sections.{id}.rosterAdmins.
 */
export const canAdminRoster = (user, sectionId) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.checkoutRosterAdminSections ?? []).includes(sectionId);
};

/**
 * Whether the user has any roster-admin rights at all. Used to gate the
 * "Rosters" tab visibility.
 */
export const hasAnyRosterAdminRights = (user) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (user.checkoutRosterAdminSections ?? []).length > 0;
};

/**
 * The list of section IDs the user can admin rosters for, intersected with
 * the catalog of roster-driven sections (excluding auto-derived ones like
 * UCO/Advancement/Dorm/OSL/ms_final where rosters don't apply).
 */
export const getMyRosterAdminSections = (user) => {
  if (!user) return [];
  const adminable = user.isSuperAdmin
    ? CHECKOUT_SECTIONS.map((s) => s.id)
    : (user.checkoutRosterAdminSections ?? []);
  return adminable.filter((sid) => {
    const cfg = getSectionConfig(sid);
    if (!cfg) return false;
    return !AUTO_SECTION_IDS.has(sid);
  });
};

/**
 * Sections whose membership is auto-derived from student attributes rather
 * than from a roster. CSV upload skips these — admins use the auto rules
 * instead.
 *
 * NOTE: 'finance' is intentionally NOT in this set on the frontend even
 * though it IS in AUTO_SECTIONS on the backend. Finance is universal (every
 * student gets a finance section at record creation) BUT it also accepts
 * roster uploads with inverted polarity (presence = pending, absence =
 * signed). Keeping finance OUT of this frontend set means the CSV upload
 * UI correctly shows a finance column for roster admins to fill in.
 *
 * 'ms_final' IS in this set — like OSL, it's auto-stamped at record
 * creation (for grades 7-8) and has no manual roster. Only signer
 * assignment matters.
 */
export const AUTO_SECTION_IDS = new Set([
  'osl',
  'ms_final',
  'uco',
  'advancement',
  'dorm',
]);

// =============================================================================
// SECTION FIELD MAP (mirrors SECTION_FIELDS in functions/checkout.js)
// =============================================================================
//
// Per-section optional CSV columns. The CSV upload reads these alongside the
// section's truthy/falsy flag column. The values get stored on the record's
// section data and surfaced in the student detail view, "My Pending" filter,
// and PDF.
//
//   contextField    — column for free-text context (which book, which device,
//                     which sport). Rendered as a "Notes" line under the
//                     section card, also included on the PDF.
//   teacherField    — column for the responsible course-teacher's display
//                     name. Surfaces on the section card and PDF.
//   teacherSisIdField — column for the SIS ID used to match teachers to
//                     their students in the "My Pending" filter.
//
// IMPORTANT: keep in sync with functions/checkout.js. Do NOT add fields here
// without also updating the backend (uploadSectionRosters extracts these
// names from rows.details).

export const SECTION_FIELDS = {
  science_books: {
    teacherField: 'science_teacher',
    teacherSisIdField: 'science_teacher_sisId',
  },
  math_books: {
    teacherField: 'math_teacher',
    teacherSisIdField: 'math_teacher_sisId',
  },
  history_books: {
    teacherField: 'history_teacher',
    teacherSisIdField: 'history_teacher_sisId',
  },
  world_lang_books: {
    teacherField: 'world_lang_teacher',
    teacherSisIdField: 'world_lang_teacher_sisId',
  },
  art_books: {
    teacherField: 'art_teacher',
    teacherSisIdField: 'art_teacher_sisId',
  },
  athletics: {
    contextField: 'athletics_sports',
  },
  library: {
    contextField: 'library_titles',
  },
  it: {
    contextField: 'it_notes',
  },
};

// All CSV column names that flag a student onto a section roster (TRUE/FALSE).
// This is the union of every roster-driven section's id, plus the special
// 'hr' alias for hr_residency (which is the literal CSV column name).
export const SECTION_FLAG_COLUMNS = (() => {
  const out = {};
  CHECKOUT_SECTIONS.forEach((section) => {
    if (AUTO_SECTION_IDS.has(section.id)) return;
    if (section.id === 'hr_residency') {
      out.hr = 'hr_residency'; // CSV uses 'hr', maps to section 'hr_residency'
    } else {
      out[section.id] = section.id;
    }
  });
  return out;
})();

// All free-text context columns and teacher columns, keyed by CSV header.
// Used by the CSV parser to recognize known column types.
export const SECTION_CONTEXT_COLUMNS = (() => {
  const out = {}; // csvColumnName → sectionId
  Object.entries(SECTION_FIELDS).forEach(([sid, def]) => {
    if (def.contextField) out[def.contextField] = sid;
  });
  return out;
})();

export const SECTION_TEACHER_COLUMNS = (() => {
  const out = {}; // csvColumnName → { sectionId, kind: 'name' | 'sisId' }
  Object.entries(SECTION_FIELDS).forEach(([sid, def]) => {
    if (def.teacherField) out[def.teacherField] = { sectionId: sid, kind: 'name' };
    if (def.teacherSisIdField) {
      out[def.teacherSisIdField] = { sectionId: sid, kind: 'sisId' };
    }
  });
  return out;
})();

// =============================================================================
// COMPLETION + STATUS HELPERS
// =============================================================================

/**
 * Compute completion percentage for a checkout record.
 * Counts only "relevant" sections (status !== 'na').
 * Signed sections count as complete; pending do not.
 *
 * Multi-teacher sections (sections with a `teachers` array): the section is
 * considered fully signed only when every teacher slot has signedAt. The
 * backend keeps section.status in sync with this, so checking status alone
 * is normally enough — but we double-check against the teachers array as
 * defense-in-depth in case a partial write left them out of sync.
 */
export const computeCompletionPercent = (record) => {
  if (!record) return 0;
  const relevantIds = getRelevantSectionIds(record);
  if (relevantIds.length === 0) return 0;

  const signedCount = relevantIds.reduce((acc, sid) => {
    const section = record.sections?.[sid];
    if (!section) return acc;
    if (section.status !== SECTION_STATUS.SIGNED) return acc;
    if (Array.isArray(section.teachers) && section.teachers.length > 0) {
      // Multi-teacher: all teachers must have signed
      if (!section.teachers.every((t) => !!t.signedAt)) return acc;
    }
    return acc + 1;
  }, 0);

  return Math.round((signedCount / relevantIds.length) * 100);
};

export const isRecordComplete = (record) =>
  computeCompletionPercent(record) === 100;

/**
 * Count pending sections excluding any section marked isFinalSignoff
 * (OSL and ms_final). Used by the final-signoff modals to decide whether
 * to show the "early signoff" warning.
 *
 * A user without override rights cannot sign a final-signoff section while
 * any non-final section is still pending — this count is what determines
 * whether the gate is open.
 */
export const countPendingNonFinalSections = (record) => {
  if (!record) return 0;
  let count = 0;
  CHECKOUT_SECTIONS.forEach((section) => {
    if (section.isFinalSignoff) return;
    const status = record.sections?.[section.id]?.status;
    if (status === SECTION_STATUS.PENDING) count += 1;
  });
  return count;
};

// Legacy alias — older callers reference this name. Kept as a stable
// re-export so component code doesn't all need a simultaneous rename.
export const countPendingNonOslSections = countPendingNonFinalSections;

/**
 * For the "My Pending" dashboard view: returns the list of section IDs in a
 * record that are still pending AND that the current user is authorized for.
 * If the result is non-empty, this record should appear in the user's pending
 * list.
 */
export const getMyPendingSectionsInRecord = (record, user) => {
  if (!record || !user) return [];
  const relevantIds = getRelevantSectionIds(record);
  return relevantIds.filter((sid) => {
    const status = record.sections?.[sid]?.status;
    if (status !== SECTION_STATUS.PENDING) return false;
    return canSignOffSection(user, sid);
  });
};

/**
 * Helper: does this section have ANY teacher slot whose sisId matches the
 * given staff SIS ID, AND that slot hasn't been signed yet?
 */
export const sectionHasMyUnsignedSlot = (section, myStaffSisId) => {
  if (!section || !myStaffSisId) return false;
  if (!Array.isArray(section.teachers)) return false;
  return section.teachers.some(
    (t) => t.sisId && String(t.sisId) === String(myStaffSisId) && !t.signedAt,
  );
};

/**
 * Tighter "my students" filter: from a list of records, return those where
 * the user is BOTH (a) authorized to sign off some pending section AND
 * (b) listed as a teacher of record on that section (via section.teachers[]
 * with my staffSisId).
 *
 * Multi-teacher aware: a teacher who is one of two on a section sees the
 * student in their My Pending until THEIR slot is signed (regardless of
 * whether the other teacher has signed).
 *
 * Falls back to the broader getMyPendingSectionsInRecord behavior — i.e.
 * include all department-pending records — when the user has NO
 * teacher-of-record matches across the whole list. This avoids punishing
 * teachers whose CSV assignments haven't been uploaded yet.
 */
export const filterRecordsByTeacherMatching = (records, user) => {
  if (!Array.isArray(records) || !user) return [];

  const myStaffSisId = user.staffSisId ? String(user.staffSisId) : null;

  const matched = [];
  const fallback = [];

  records.forEach((record) => {
    const myPendingSectionIds = getMyPendingSectionsInRecord(record, user);
    if (myPendingSectionIds.length === 0) return;

    if (myStaffSisId) {
      const hasTeacherMatch = myPendingSectionIds.some((sid) => {
        const section = record.sections?.[sid];
        return sectionHasMyUnsignedSlot(section, myStaffSisId);
      });
      if (hasTeacherMatch) {
        matched.push(record);
        return;
      }
    }
    fallback.push(record);
  });

  return matched.length > 0 ? matched : fallback;
};

// =============================================================================
// RECORD CONSTRUCTION
// =============================================================================

/**
 * Build the initial `sections` map for a new checkout record.
 * Sections relevant to the form/cohort start as 'pending'; others are 'na'.
 * Dorm sections include an empty inventory map.
 *
 * NOTE: real record creation goes through the assignCheckoutForm Cloud
 * Function, which calls buildSectionsForRecord in functions/checkout.js.
 * This frontend helper is legacy — there are no remaining callers — but
 * is preserved for any future modal preview or test fixture use.
 */
export const buildInitialSections = (formId, cohort) => {
  const relevantIds = new Set(getDefaultSectionIdsForForm(formId, cohort));
  const sections = {};

  CHECKOUT_SECTIONS.forEach((section) => {
    const isRelevant = relevantIds.has(section.id);
    const base = {
      status: isRelevant ? SECTION_STATUS.PENDING : SECTION_STATUS.NA,
      signedBy: null,
      signedByName: null,
      signedAt: null,
      notes: '',
      signoffHistory: [],
    };
    if (section.hasInventoryChecklist) {
      base.inventory = DORM_INVENTORY_ITEMS.reduce((acc, item) => {
        acc[item.id] = false;
        return acc;
      }, {});
      base.damageNotes = '';
    }
    sections[section.id] = base;
  });

  return sections;
};

/**
 * When a form type is changed mid-flight, migrate the existing sections map
 * to fit the new form. Sections that exist in both the old and new form are
 * preserved (status, signoff data, inventory). Sections that no longer apply
 * are reset to N/A. Sections that are newly relevant become pending.
 *
 * Called from the changeCheckoutForm Cloud Function and replicated here so
 * the change-modal can show a preview diff to the superadmin.
 */
export const migrateSectionsForFormChange = (existingSections, newFormId, cohort) => {
  const newRelevantIds = new Set(getDefaultSectionIdsForForm(newFormId, cohort));
  const migrated = {};

  CHECKOUT_SECTIONS.forEach((section) => {
    const existing = existingSections?.[section.id];
    const isRelevantNow = newRelevantIds.has(section.id);

    if (!existing) {
      const base = {
        status: isRelevantNow ? SECTION_STATUS.PENDING : SECTION_STATUS.NA,
        signedBy: null,
        signedByName: null,
        signedAt: null,
        notes: '',
        signoffHistory: [],
      };
      if (section.hasInventoryChecklist) {
        base.inventory = DORM_INVENTORY_ITEMS.reduce((acc, item) => {
          acc[item.id] = false;
          return acc;
        }, {});
        base.damageNotes = '';
      }
      migrated[section.id] = base;
      return;
    }

    if (isRelevantNow) {
      migrated[section.id] = existing;
    } else {
      migrated[section.id] = { ...existing, status: SECTION_STATUS.NA };
    }
  });

  return migrated;
};

// =============================================================================
// SCHOOL YEAR HELPERS
// =============================================================================

/**
 * Default school year if /config/systemSettings doesn't exist yet.
 * Per Kyle's instructions, seed with this on first boot.
 */
export const DEFAULT_SCHOOL_YEAR = '2025-2026';

/**
 * Build the deterministic Firestore doc ID for a checkout record.
 * Format: "<schoolYear>_<sisId>" → e.g. "2025-2026_100476"
 */
export const buildCheckoutRecordId = (schoolYear, sisId) =>
  `${schoolYear}_${sisId}`;

/**
 * Suggest the next school year string given the current one.
 * '2025-2026' → '2026-2027'
 */
export const nextSchoolYear = (currentYear) => {
  if (!currentYear || !/^\d{4}-\d{4}$/.test(currentYear)) return null;
  const [start, end] = currentYear.split('-').map(Number);
  return `${start + 1}-${end + 1}`;
};

// =============================================================================
// FORMATTING HELPERS
// =============================================================================

/**
 * Format a Firestore timestamp (or Date, or null) into a readable string.
 */
export const formatSignoffTimestamp = (ts) => {
  if (!ts) return '';
  let date;
  if (typeof ts.toDate === 'function') date = ts.toDate();
  else if (ts instanceof Date) date = ts;
  else if (typeof ts === 'number') date = new Date(ts);
  else if (typeof ts === 'string') date = new Date(ts);
  else return '';

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const getStatusLabel = (status) => {
  switch (status) {
    case SECTION_STATUS.SIGNED:
      return 'Signed';
    case SECTION_STATUS.NA:
      return 'N/A';
    case SECTION_STATUS.PENDING:
    default:
      return 'Pending';
  }
};

export const getStatusColor = (status) => {
  switch (status) {
    case SECTION_STATUS.SIGNED:
      return { bg: '#D4EDDA', fg: '#155724', border: '#C3E6CB' };
    case SECTION_STATUS.NA:
      return { bg: '#E9ECEF', fg: '#6C757D', border: '#DEE2E6' };
    case SECTION_STATUS.PENDING:
    default:
      return { bg: '#FFF3CD', fg: '#856404', border: '#FFEEBA' };
  }
};

// =============================================================================
// KA BRAND CONSTANTS
// =============================================================================

export const KA_COLORS = {
  red: '#E83530',
  navy: '#233C5F',
  gold: '#A29164',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  borderGray: '#E0E0E0',
};